import React, { createContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePrayerTimes } from './PrayerTimesContext';
import * as notificationService from '../services/notificationService';

// Default athan settings
const DEFAULT_SETTINGS = {
  enabled: false,
  notificationsEnabled: false,
  volume: 100,
  specialFajrAthan: true,
  preAthanReminder: false,
  reminderMinutes: 10,
  athanPath: 'https://praytimes.org/audio/adhan/Sunni/Adhan%20Makkah.mp3',
  fajrAthanPath: 'https://archive.org/download/adhan_fajr_mansour_zahrani/adhan_fajr_mansour_zahrani.mp3',
  playingAudio: null,
  // Personalization settings
  customReminderTimes: {
    fajr: 20,    // 20 minutes before Fajr by default
    dhuhr: 10,   // 10 minutes before other prayers by default
    asr: 10,
    maghrib: 10,
    isha: 10
  },
  customRemindersEnabled: false
};

// Available athans list
export const AVAILABLE_ATHANS = [
  {
    id: 'makkah',
    name: 'Makkah - Sheikh Ali Mulla',
    url: 'https://praytimes.org/audio/adhan/Sunni/Adhan%20Makkah.mp3'
  },
  {
    id: 'madinah',
    name: 'Madinah - Athan',
    url: 'https://praytimes.org/audio/adhan/Sunni/Adhan%20Madina.mp3'
  },
  {
    id: 'egyptian',
    name: 'Egyptian - Sheikh Mohammed Rifat',
    url: 'https://praytimes.org/audio/adhan/Sunni/Adhan%20Egypt.mp3'
  },
  {
    id: 'abdulbasit',
    name: 'Egyptian - Abdul Basit',
    url: 'https://praytimes.org/audio/adhan/Sunni/Abdul-Basit.mp3'
  },
  {
    id: 'hafez',
    name: 'Hafez',
    url: 'https://praytimes.org/audio/adhan/Sunni/Hafez.mp3'
  },
  {
    id: 'mansour',
    name: 'Mansour Zahrani',
    url: 'https://dl.dropboxusercontent.com/scl/fi/lb57ysf7u4ss43pdxye3c/mansour_al_zahrani.mp3?rlkey=3wmv9aba0eb4rwiu58fva0iy0&st=sc8yfbzh&dl=1'
  },
  {
    id: 'naseer-alqatami',
    name: 'Naseer Al Qatami',
    url: 'https://download.tvquran.com/download/TvQuran.com__Athan/TvQuran.com__08.athan.mp3'
  },
  {
    id: 'ahmad-al-nafees',
    name: 'Ahmad Al Nafees',
    url: 'https://dl.dropboxusercontent.com/scl/fi/3jiy3o1hx19rss0hc9je7/ahmad_al_nafees.mp3?rlkey=doljk0aon9gdqt3tiayr1hgtu&st=naujvp66&dl=1'
  },
  {
    id: 'hafiz-mustfa-ozcan',
    name: 'Hafiz Mustfa Ozcan',
    url: 'https://dl.dropboxusercontent.com/scl/fi/fd2tmxv0v84qnlobeqmhp/hafiz_mustafa_ozcan.mp3?rlkey=i0ao0cr1t0zosb37lc83hqhjh&st=knjy3z9q&dl=1'
  },
  {
    id: 'karl-jenkins',
    name: 'Karl Jenkins',
    url: 'https://dl.dropboxusercontent.com/scl/fi/vlxw4ue8wos0sujhucfqi/karl_jenkins_mass.mp3?rlkey=9ca7gmildknq3vjqupguwh4cx&st=yni4511s&dl=1'
  },
  {
    id: 'rashid-alafasy',
    name: 'Mishary Rashid Alafasy',
    url: 'https://dl.dropboxusercontent.com/scl/fi/di43o6wudlbqy04pg6vl3/mishary_rashid_alafassay.mp3?rlkey=0fszlwrb291xwaoxit8yo8trk&st=4i8wbuhk&dl=1'
  },
  {
    id: 'rashid-alafasy-dubai',
    name: 'Mishary Rashid Alafasy Dubai',
    url: 'https://dl.dropboxusercontent.com/scl/fi/rfbkqp8b4o45pjuk679qo/mishary_rashid_dubai.mp3?rlkey=bsp0f4jbrbdzl3ecgq0tujpe9&st=8xcbvone&dl=1'
  },
];

export const AVAILABLE_FAJR_ATHANS = [
  {
    id: 'mansour-fajr',
    name: 'Mansour Zahrani Fajr',
    url: 'https://archive.org/download/adhan_fajr_mansour_zahrani/adhan_fajr_mansour_zahrani.mp3'
  }
];

// Create context
export const AthanSettingsContext = createContext();

export const AthanSettingsProvider = ({ children }) => {
  // Get prayer times from context
  const { prayerTimes, currentDay } = usePrayerTimes();
  
  // Saved scheduled notifications for cleanup
  const scheduledNotificationsRef = useRef([]);
  
  // Load saved settings from localStorage
  const [settings, setSettings] = useState(() => {
    try {
      const savedSettings = localStorage.getItem('athan-settings');
      return savedSettings ? { ...DEFAULT_SETTINGS, ...JSON.parse(savedSettings) } : DEFAULT_SETTINGS;
    } catch (error) {
      console.error('Error loading athan settings:', error);
      return DEFAULT_SETTINGS;
    }
  });
  
  // Check notification permission on mount
  useEffect(() => {
    if (settings.notificationsEnabled) {
      const permissionStatus = notificationService.getNotificationPermissionStatus();
      if (permissionStatus !== 'granted') {
        // Permissions were revoked externally, update our state
        updateSettings({ notificationsEnabled: false });
      }
    }
  }, [settings.notificationsEnabled]);
  
  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem('athan-settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving athan settings:', error);
    }
  }, [settings]);
  
  // Schedule notifications when prayer times change or settings are updated
  useEffect(() => {
    if (!settings.enabled || !prayerTimes || prayerTimes.length === 0) {
      return;
    }
    
    // Clear existing scheduled notifications
    notificationService.clearScheduledNotifications(scheduledNotificationsRef.current);
    scheduledNotificationsRef.current = [];
    
    // Schedule new notifications if enabled
    if (settings.notificationsEnabled) {
      scheduledNotificationsRef.current = notificationService.schedulePrayerNotifications(
        prayerTimes, 
        settings
      );
      
      console.log(`Scheduled ${scheduledNotificationsRef.current.length} prayer notifications`);
    }
    
    // Cleanup on unmount
    return () => {
      notificationService.clearScheduledNotifications(scheduledNotificationsRef.current);
    };
  }, [prayerTimes, settings, currentDay]);
  
  // Update settings function
  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };
  
  // Request notification permissions
  const requestNotificationPermission = async () => {
    const result = await notificationService.requestNotificationPermission();
    if (result.granted) {
      updateSettings({ notificationsEnabled: true });
    }
    return result;
  };
  
  // Play Athan manually
  const playAthan = (isSpecialFajr = false) => {
    // Stop any currently playing audio
    if (settings.playingAudio) {
      settings.playingAudio.pause();
      settings.playingAudio.currentTime = 0;
    }
    
    // Determine which audio file to play
    const audioPath = isSpecialFajr && settings.specialFajrAthan
      ? settings.fajrAthanPath
      : settings.athanPath;
    
    // Play the audio
    const audio = notificationService.playAthanSound(audioPath);
    if (audio) {
      // Set volume according to settings
      audio.volume = settings.volume / 100;
      
      // Save reference to playing audio
      updateSettings({ playingAudio: audio });
      
      // Clear reference when audio finishes
      audio.addEventListener('ended', () => {
        updateSettings({ playingAudio: null });
      });
    }
    
    return audio;
  };
  
  // Stop currently playing Athan
  const stopAthan = () => {
    if (settings.playingAudio) {
      settings.playingAudio.pause();
      settings.playingAudio.currentTime = 0;
      updateSettings({ playingAudio: null });
      return true;
    }
    return false;
  };
  
  // Context value
  const value = {
    settings,
    updateSettings,
    requestNotificationPermission,
    playAthan,
    stopAthan,
    availableAthans: AVAILABLE_ATHANS,
    availableFajrAthans: AVAILABLE_FAJR_ATHANS
  };
  
  return (
    <AthanSettingsContext.Provider value={value}>
      {children}
    </AthanSettingsContext.Provider>
  );
};

export default AthanSettingsContext;